import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getRunPayload } from '@/services/runStore';

type RunPayload = {
  code: string;
  language: string;
  originUrl?: string;
  createdAt: number;
};

const buildSrcDoc = (payload: RunPayload) => {
  const lang = (payload.language || '').toLowerCase();
  const userCode = payload.code || '';

  const normalizeTsToJs = (tsOrJs: string) => {
    // This runner does not bundle/compile TS; it will attempt to execute as JS.
    // Most TS code will fail in the browser unless already JS-compatible.
    return tsOrJs;
  };

  const htmlBody = lang === 'html' ? userCode : '';
  const cssText = lang === 'css' ? userCode : '';
  const jsText = ['javascript', 'js', 'typescript', 'ts'].includes(lang) ? normalizeTsToJs(userCode) : '';

  const defaultHtmlForCss = `
    <div class="__runner">
      <h1>CSS Preview</h1>
    </div>
  `;

  const defaultHtmlForJs = `
    <div class="__runner">
    </div>
  `;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Run</title>
    <style>
      html, body {
        height: 100%;
      }
      body {
        margin: 0;
        font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
        background: #ffffff;
        color: #000000;
      }
      .__runner {
        padding: 18px;
      }

      /* User CSS */
      ${cssText}
    </style>
  </head>
  <body>
    ${lang === 'css' ? defaultHtmlForCss : ''}
    ${lang === 'html' ? htmlBody : ''}
    ${['javascript', 'js', 'typescript', 'ts'].includes(lang) ? defaultHtmlForJs : ''}

    <script>
      (function() {
        const outputEl = document.getElementById('__output');

        const STATIC_IMAGES = [
          'https://static.photos/blurred/1024x576',
          'https://static.photos/vintage/1024x576',
          'https://static.photos/gradient/1024x576',
          'https://static.photos/minimal/1024x576',
        ];

        let __imgQueue = STATIC_IMAGES.slice().sort(() => Math.random() - 0.5);
        let __imgPtr = 0;
        let __imgTryIndex = 0;
        let __imgTryUrls = [];

        const getRandomStaticPhotoUrl = (baseUrl) => {
          const rand = Math.random().toString(36).slice(2);
          return baseUrl + (baseUrl.includes('?') ? '&' : '?') + 'r=' + rand;
        };

        const makeTwoTryUrls = (baseUrl) => {
          const a = getRandomStaticPhotoUrl(baseUrl);
          const b = getRandomStaticPhotoUrl(baseUrl);
          return a === b ? [a, getRandomStaticPhotoUrl(baseUrl)] : [a, b];
        };

        const getNextFallbackImageUrl = () => {
          if (__imgTryUrls.length === 0 || __imgTryIndex >= __imgTryUrls.length) {
            if (__imgPtr >= __imgQueue.length) {
              __imgQueue = STATIC_IMAGES.slice().sort(() => Math.random() - 0.5);
              __imgPtr = 0;
            }
            const base = __imgQueue[__imgPtr] || STATIC_IMAGES[0];
            __imgPtr += 1;
            __imgTryUrls = makeTwoTryUrls(base);
            __imgTryIndex = 0;
          }

          const url = __imgTryUrls[__imgTryIndex];
          __imgTryIndex += 1;
          return url;
        };

        const isBrokenImage = (img) => {
          try {
            if (!img) return false;
            if (!(img instanceof HTMLImageElement)) return false;
            if (!img.currentSrc && !img.src) return false;
            if (img.complete && img.naturalWidth > 0) return false;
            // If complete but width 0 => broken
            if (img.complete && img.naturalWidth === 0) return true;
            // If not complete, we will treat it as potentially stuck and re-check later
            return false;
          } catch (_) {
            return false;
          }
        };

        const patchImage = (img) => {
          try {
            if (!img || !(img instanceof HTMLImageElement)) return;
            if (img.dataset.__oreogPatched === '1') return;
            img.dataset.__oreogPatched = '1';

            const rescue = () => {
              const attempts = Number(img.dataset.__oreogAttempts || '0');
              if (attempts >= 6) return;
              img.dataset.__oreogAttempts = String(attempts + 1);
              const nextUrl = getNextFallbackImageUrl();
              img.src = nextUrl;
            };

            img.addEventListener('error', () => {
              rescue();
            });

            // Some failures don't reliably trigger error; re-check shortly after load
            window.setTimeout(() => {
              if (isBrokenImage(img)) rescue();
            }, 1200);
            window.setTimeout(() => {
              if (isBrokenImage(img)) rescue();
            }, 3500);
          } catch (_) {
            // ignore
          }
        };

        const patchAllImages = () => {
          try {
            document.querySelectorAll('img').forEach(patchImage);
          } catch (_) {
            // ignore
          }
        };

        patchAllImages();
        const mo = new MutationObserver(() => patchAllImages());
        try {
          mo.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['src'] });
        } catch (_) {
          // ignore
        }

        const safeToString = (v) => {
          try {
            if (typeof v === 'string') return v;
            return JSON.stringify(v, null, 2);
          } catch (_) {
            return String(v);
          }
        };
        const append = (text) => {
          if (!outputEl) return;
          outputEl.textContent += text + '\n';
        };

        // Mirror console logs into output
        const originalLog = console.log;
        const originalWarn = console.warn;
        const originalError = console.error;
        console.log = (...args) => { originalLog(...args); append(args.map(safeToString).join(' ')); };
        console.warn = (...args) => { originalWarn(...args); append('[warn] ' + args.map(safeToString).join(' ')); };
        console.error = (...args) => { originalError(...args); append('[error] ' + args.map(safeToString).join(' ')); };

        window.addEventListener('error', (e) => {
          append('[runtime error] ' + (e && e.message ? e.message : 'Unknown error'));
        });

        // Convenience print()
        window.print = (...args) => append(args.map(safeToString).join(' '));

        try {
          ${jsText}
        } catch (e) {
          append('[exception] ' + (e && e.message ? e.message : String(e)));
        }
      })();
    </script>
  </body>
</html>`;
};

const Run = () => {
  const { runId } = useParams();
  const [payload, setPayload] = useState<RunPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) {
      setError('Missing run id.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const parsed = await getRunPayload(runId);
        if (cancelled) return;
        if (!parsed) {
          setError('This run session was not found on this device. Re-run the code to generate a new session.');
          return;
        }
        if (!parsed?.code || !parsed?.language) {
          setError('Invalid run payload. Re-run the code.');
          return;
        }
        setPayload(parsed);
      } catch (e) {
        if (cancelled) return;
        setError('Failed to load run session.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [runId]);

  const srcDoc = useMemo(() => {
    if (!payload) return '';
    return buildSrcDoc(payload);
  }, [payload]);

  const handleBack = () => {
    const originUrl = payload?.originUrl;

    try {
      if (window.opener && !window.opener.closed) {
        try {
          if (originUrl) {
            window.opener.location.href = originUrl;
          }
        } catch {
          // ignore cross-origin or navigation errors
        }

        try {
          window.opener.focus();
        } catch {
          // ignore
        }

        window.close();
        return;
      }
    } catch {
      // ignore
    }

    if (originUrl) {
      window.location.href = originUrl;
      return;
    }

    window.history.back();
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="w-full max-w-xl border border-border/50 bg-card rounded-2xl p-5">
          <div className="text-lg font-semibold">Run</div>
          <div className="mt-2 text-sm text-muted-foreground">{error}</div>
          <a
            href="/"
            className="mt-4 inline-flex items-center justify-center rounded-xl px-4 py-2 bg-muted hover:bg-muted/80 text-sm"
          >
            Go back
          </a>
        </div>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-[98vh] bg-white text-black">
      <div className="mx-auto max-w-full h-full">
        <div className="overflow-hidden h-full bg-white">
          <iframe
            title="Runner"
            className="w-full h-[98vh] bg-white"
            sandbox="allow-scripts allow-forms allow-modals allow-popups allow-downloads"
            srcDoc={srcDoc}
          />
        </div>
      </div>
    </div>
  );
};

export default Run;
